import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { sourcingAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Loader2, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { DRAFT_STATUS_BADGE, OUTCOME_BADGE, SOURCING_CURRENCIES, badgeClass } from './sourcingUtils';

const LINE_OUTCOMES = ['QUOTED', 'NO_STOCK', 'DECLINED', 'NO_REPLY', 'PENDING'];
const FILTERS = ['ALL', 'PENDING', 'QUOTED', 'NO_STOCK', 'DECLINED', 'NO_REPLY'];

function errorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join(', ');
  if (typeof detail === 'string') return detail;
  return fallback;
}

function emptyLineDraft() {
  return { status: 'PENDING', price: '', qty: '', currency: 'USD', note: '' };
}

function lineDraftFromOutcome(outcome) {
  if (!outcome) return emptyLineDraft();
  return {
    status: outcome.status || 'PENDING', price: outcome.price ?? '', qty: outcome.qty ?? '',
    currency: outcome.currency || 'USD', note: outcome.note || '',
  };
}

export default function RfqLogTab() {
  const { canAccess } = useAuth();
  const canEdit = canAccess('SOURCING', 'EDIT');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('mine');
  const [outcomeFilter, setOutcomeFilter] = useState('ALL');
  const [rfqs, setRfqs] = useState([]);
  const [caps, setCaps] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [loggingId, setLoggingId] = useState(null);
  const [lineDrafts, setLineDrafts] = useState({}); // `${rfqId}:${lineId}` -> draft
  const [bulkStatus, setBulkStatus] = useState({}); // rfqId -> status
  const [busyKey, setBusyKey] = useState(null);
  const [sentToBidEngine, setSentToBidEngine] = useState({});

  // Addendum B (30 Oct 2026, item B): "My products" filters.
  const [prodFilters, setProdFilters] = useState({ sku_id: '', company: '', sender: '', from: '', to: '' });
  const [prodLines, setProdLines] = useState([]);
  const prodFiltersRef = useRef(prodFilters);
  useEffect(() => { prodFiltersRef.current = prodFilters; }, [prodFilters]);

  useEffect(() => {
    sourcingAPI.getCapabilities().then((res) => setCaps(res.data)).catch(() => {});
  }, []);

  const load = useCallback((v = view) => {
    setLoading(true);
    if (v === 'products') {
      // read via ref, not the prodFilters closure — Filter button always sends the
      // LATEST typed values, and typing a filter never re-triggers this effect itself
      sourcingAPI.listRfqs({ view: 'products', ...prodFiltersRef.current })
        .then((res) => setProdLines(res.data.lines))
        .catch(() => toast.error('Failed to load My products'))
        .finally(() => setLoading(false));
      return;
    }
    sourcingAPI.listRfqs({ view: v })
      .then((res) => setRfqs(res.data.rfqs))
      .catch(() => toast.error('Failed to load RFQ log'))
      .finally(() => setLoading(false));
  }, [view]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (rfqId) => setExpandedId((prev) => (prev === rfqId ? null : rfqId));

  const startLogging = (rfq) => {
    setLoggingId(rfq.id);
    setExpandedId(rfq.id);
    const drafts = {};
    (rfq.lines || []).forEach((l) => { drafts[`${rfq.id}:${l.id}`] = lineDraftFromOutcome(l.outcome); });
    setLineDrafts((prev) => ({ ...prev, ...drafts }));
    setBulkStatus((prev) => ({ ...prev, [rfq.id]: 'NO_REPLY' }));
  };

  const setLineDraft = (rfqId, lineId, patch) => {
    const key = `${rfqId}:${lineId}`;
    setLineDrafts((prev) => ({ ...prev, [key]: { ...(prev[key] || emptyLineDraft()), ...patch } }));
  };

  const saveLineOutcome = async (rfqId, lineId) => {
    const draft = lineDrafts[`${rfqId}:${lineId}`] || emptyLineDraft();
    if (draft.status === 'QUOTED') {
      if (draft.qty !== '' && (!Number.isInteger(Number(draft.qty)) || Number(draft.qty) < 0)) {
        toast.error('Qty must be a whole number, 0 or more'); return;
      }
      if (draft.price !== '' && Number(draft.price) < 0) {
        toast.error('Price must be 0 or more'); return;
      }
    }
    const key = `${rfqId}:${lineId}`;
    setBusyKey(key);
    try {
      const res = await sourcingAPI.setLineOutcome(rfqId, lineId, {
        status: draft.status,
        price: draft.price !== '' ? Number(draft.price) : null,
        qty: draft.qty !== '' ? Number(draft.qty) : null,
        currency: draft.currency || 'USD', note: draft.note || '',
      });
      setRfqs((prev) => prev.map((r) => (r.id === rfqId ? res.data : r)));
      toast.success('Line outcome saved');
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save line outcome'));
    } finally {
      setBusyKey(null);
    }
  };

  const applyBulk = async (rfqId) => {
    const status = bulkStatus[rfqId] || 'NO_REPLY';
    setBusyKey(`bulk:${rfqId}`);
    try {
      const res = await sourcingAPI.setBulkLineOutcome(rfqId, { status, currency: 'USD' });
      setRfqs((prev) => prev.map((r) => (r.id === rfqId ? res.data : r)));
      const drafts = {};
      (res.data.lines || []).forEach((l) => { drafts[`${rfqId}:${l.id}`] = lineDraftFromOutcome(l.outcome); });
      setLineDrafts((prev) => ({ ...prev, ...drafts }));
      toast.success(`"${status.replace('_', ' ')}" applied to every line`);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not apply to all lines'));
    } finally {
      setBusyKey(null);
    }
  };

  const sendToBidEngine = async (rfqId) => {
    setBusyKey(`bid:${rfqId}`);
    try {
      await sourcingAPI.sendRfqToBidEngine(rfqId);
      setSentToBidEngine((prev) => ({ ...prev, [rfqId]: true }));
      toast.success('Sent to Bid Engine');
    } catch (err) {
      toast.error(errorMessage(err, 'Could not send to Bid Engine'));
    } finally {
      setBusyKey(null);
    }
  };

  const showProducts = !!(caps && (caps.own_sku_count > 0 || canEdit));

  if (loading) return <div className="flex justify-center py-16" data-testid="rfqs-loading"><Loader2 className="animate-spin" /></div>;

  const visibleRfqs = rfqs.filter((r) => {
    if (outcomeFilter === 'ALL') return true;
    if (outcomeFilter === 'PENDING') return r.status === 'SENT' && (!r.outcome || r.outcome === 'PENDING');
    return r.outcome === outcomeFilter;
  });

  return (
    <div className="space-y-3" data-testid="sourcing-rfqs-tab">
      <div className="flex gap-2" data-testid="rfqs-view-switcher">
        <Button size="sm" variant={view === 'mine' ? 'default' : 'outline'} onClick={() => setView('mine')} data-testid="rfqs-view-mine-btn">Mine</Button>
        {canEdit && <Button size="sm" variant={view === 'all' ? 'default' : 'outline'} onClick={() => setView('all')} data-testid="rfqs-view-all-btn">All</Button>}
        {showProducts && (
          <Button size="sm" variant={view === 'products' ? 'default' : 'outline'} onClick={() => setView('products')} data-testid="rfqs-view-products-btn">My products</Button>
        )}
      </div>

      {view === 'products' ? (
        <>
          <div className="flex flex-wrap gap-2" data-testid="products-filters">
            <Input placeholder="SKU" className="w-40 h-8" value={prodFilters.sku_id}
              onChange={(e) => setProdFilters((f) => ({ ...f, sku_id: e.target.value }))} data-testid="products-filter-sku" />
            <Input placeholder="Vendor (company id)" className="w-48 h-8" value={prodFilters.company}
              onChange={(e) => setProdFilters((f) => ({ ...f, company: e.target.value }))} data-testid="products-filter-vendor" />
            <Input placeholder="Sender (user id)" className="w-44 h-8" value={prodFilters.sender}
              onChange={(e) => setProdFilters((f) => ({ ...f, sender: e.target.value }))} data-testid="products-filter-sender" />
            <Input type="date" className="w-40 h-8" value={prodFilters.from}
              onChange={(e) => setProdFilters((f) => ({ ...f, from: e.target.value }))} data-testid="products-filter-from" />
            <Input type="date" className="w-40 h-8" value={prodFilters.to}
              onChange={(e) => setProdFilters((f) => ({ ...f, to: e.target.value }))} data-testid="products-filter-to" />
            <Button size="sm" onClick={() => load('products')} data-testid="products-filter-apply-btn">Filter</Button>
          </div>
          <Card className="rounded-sm">
            <CardContent className="p-0 divide-y divide-border">
              {prodLines.map((l) => (
                <div key={`${l.rfq_id}-${l.line_id}`} className="p-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`product-line-${l.rfq_id}-${l.line_id}`}>
                  <span className="font-mono">{l.sku_id}</span>
                  <span className="text-muted-foreground">{l.part_number}</span>
                  <span className="text-muted-foreground">{l.vendor}</span>
                  <span className="text-xs text-muted-foreground">
                    Sent by {l.sent_by_name}{l.sent_for_name ? ` for ${l.sent_for_name}` : ''} · {l.sent_at && new Date(l.sent_at).toLocaleDateString()}
                  </span>
                  <span className="text-xs">Qty {l.qty}</span>
                  <span className="text-xs">{l.price_sent ? `Our price: $${l.price_sent}` : 'No price shared'}</span>
                  {l.outcome?.status && (
                    <Badge variant="outline" className={badgeClass(OUTCOME_BADGE, l.outcome.status)}>{l.outcome.status.replace('_', ' ')}</Badge>
                  )}
                </div>
              ))}
              {prodLines.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground" data-testid="products-empty-state">No RFQ lines for your products yet.</div>}
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          <div className="flex flex-wrap gap-2" data-testid="rfqs-outcome-filter">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setOutcomeFilter(f)}
                data-testid={`rfqs-outcome-filter-${f.toLowerCase()}`}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  outcomeFilter === f ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:border-foreground/40'
                }`}
              >
                {f.replace('_', ' ')}
              </button>
            ))}
          </div>
          <Card className="rounded-sm">
            <CardContent className="p-0 divide-y divide-border">
              {visibleRfqs.map((r) => (
                <div key={r.id} className="p-4 space-y-2" data-testid={`rfq-row-${r.id}`}>
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{r.subject}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(r.sent_at).toLocaleString()} · {r.lines?.length || 0} line(s)
                        {r.sent_by_name && (
                          <span data-testid={`rfq-sender-${r.id}`}> · Sent by {r.sent_by_name}{r.sent_for_name ? ` for ${r.sent_for_name}` : ''}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className={badgeClass(DRAFT_STATUS_BADGE, r.status)}>{r.status}</Badge>
                      {r.outcome && <Badge variant="outline" className={badgeClass(OUTCOME_BADGE, r.outcome)} data-testid={`rfq-outcome-badge-${r.id}`}>{r.outcome.replace('_', ' ')}</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="ghost" onClick={() => toggleExpand(r.id)} data-testid={`rfq-expand-btn-${r.id}`}>
                      {expandedId === r.id ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />} Lines
                    </Button>
                    {r.status === 'SENT' && (
                      <Button size="sm" variant="outline" onClick={() => startLogging(r)} data-testid={`rfq-log-outcome-btn-${r.id}`}>
                        {r.outcome ? 'Update Outcomes' : 'Log Outcomes'}
                      </Button>
                    )}
                    {r.outcome === 'QUOTED' && (
                      <Button size="sm" variant="outline" onClick={() => sendToBidEngine(r.id)} disabled={busyKey === `bid:${r.id}` || sentToBidEngine[r.id]} data-testid={`rfq-send-bid-engine-btn-${r.id}`}>
                        <Send className="w-3.5 h-3.5 mr-1" /> {sentToBidEngine[r.id] ? 'Sent to Bid Engine' : 'Send to Bid Engine'}
                      </Button>
                    )}
                  </div>

                  {expandedId === r.id && (
                    <div className="space-y-2" data-testid={`rfq-lines-detail-${r.id}`}>
                      {loggingId === r.id && (
                        <div className="rounded-sm border border-border p-2 flex items-center gap-2 flex-wrap bg-muted/20" data-testid={`rfq-bulk-outcome-${r.id}`}>
                          <span className="text-xs text-muted-foreground">Same for all lines:</span>
                          <select className="border border-border rounded-sm text-xs px-2 py-1 bg-background" value={bulkStatus[r.id] || 'NO_REPLY'}
                            onChange={(e) => setBulkStatus((prev) => ({ ...prev, [r.id]: e.target.value }))} data-testid={`rfq-bulk-outcome-select-${r.id}`}>
                            {LINE_OUTCOMES.map((o) => <option key={o} value={o}>{o.replace('_', ' ')}</option>)}
                          </select>
                          <Button size="sm" onClick={() => applyBulk(r.id)} disabled={busyKey === `bulk:${r.id}`} data-testid={`rfq-bulk-outcome-apply-btn-${r.id}`}>Apply to all</Button>
                        </div>
                      )}
                      {(r.lines || []).map((l) => {
                        const key = `${r.id}:${l.id}`;
                        const draft = lineDrafts[key] || lineDraftFromOutcome(l.outcome);
                        return (
                          <div key={l.id} className="rounded-sm border border-border p-2 space-y-2" data-testid={`rfq-line-detail-${l.id}`}>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                              <span className="font-mono font-medium">{l.sku_id}</span>
                              <span className="text-muted-foreground">Part: {l.part_number || l.search_part || '-'}</span>
                              <span className="text-muted-foreground">Mfg: {l.manufacturer || '-'}</span>
                              <span className="text-muted-foreground">Cond: {l.condition || 'NEW'}</span>
                              <span className="text-muted-foreground">Qty sent: {l.qty}</span>
                              <span className="text-muted-foreground">Vendor qty: {l.listed_qty ?? '-'}</span>
                              <span className="text-muted-foreground">Vendor price: {l.price != null ? `$${l.price}` : '-'}</span>
                              <span className="text-muted-foreground">Target: {l.target_price_usd != null ? `$${l.target_price_usd}` : '-'}</span>
                              <span className="text-muted-foreground" data-testid={`rfq-line-price-sent-${l.id}`}>
                                {l.price_sent ? `Our price: $${l.price_sent}` : 'No price shared'}
                              </span>
                              {l.outcome?.status && (
                                <Badge variant="outline" className={badgeClass(OUTCOME_BADGE, l.outcome.status)} data-testid={`rfq-line-outcome-badge-${l.id}`}>{l.outcome.status.replace('_', ' ')}</Badge>
                              )}
                            </div>
                            {loggingId === r.id && (
                              <div className="flex flex-wrap items-end gap-2">
                                {LINE_OUTCOMES.map((o) => (
                                  <button key={o} onClick={() => setLineDraft(r.id, l.id, { status: o })}
                                    data-testid={`rfq-line-outcome-option-${l.id}-${o.toLowerCase()}`}
                                    className={`px-2 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                                      draft.status === o ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:border-foreground/40'
                                    }`}>
                                    {o.replace('_', ' ')}
                                  </button>
                                ))}
                                {draft.status === 'QUOTED' && (
                                  <>
                                    <Input placeholder="Price" type="number" min="0" step="0.01" className="w-20 h-7"
                                      value={draft.price} onChange={(e) => setLineDraft(r.id, l.id, { price: e.target.value })}
                                      data-testid={`rfq-line-quote-price-${l.id}`} />
                                    <Input placeholder="Qty" type="number" min="0" step="1" className="w-16 h-7"
                                      value={draft.qty} onChange={(e) => setLineDraft(r.id, l.id, { qty: e.target.value })}
                                      data-testid={`rfq-line-quote-qty-${l.id}`} />
                                    <select className="border border-border rounded-sm text-xs px-1 h-7 bg-background"
                                      value={draft.currency} onChange={(e) => setLineDraft(r.id, l.id, { currency: e.target.value })}
                                      data-testid={`rfq-line-quote-currency-${l.id}`}>
                                      {SOURCING_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                    <Input placeholder="Note" className="w-32 h-7" value={draft.note}
                                      onChange={(e) => setLineDraft(r.id, l.id, { note: e.target.value })}
                                      data-testid={`rfq-line-quote-note-${l.id}`} />
                                  </>
                                )}
                                <Button size="sm" className="h-7" onClick={() => saveLineOutcome(r.id, l.id)} disabled={busyKey === key} data-testid={`rfq-line-save-outcome-btn-${l.id}`}>Save</Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {loggingId === r.id && (
                        <Button size="sm" variant="ghost" onClick={() => setLoggingId(null)} data-testid={`rfq-cancel-outcome-btn-${r.id}`}>Done</Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {visibleRfqs.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground" data-testid="rfqs-empty-state">No RFQs match this filter.</div>}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
